import React, { useState } from 'react';
import { Edit2, Trash2 } from 'lucide-react';
import { DeleteConfirmModal } from '../DeleteConfirmModal';
import type { Surgery } from '../../types/clinical';

export interface SurgeryCardProps {
  surgery: Surgery;
  isDoctor: boolean;
  editingSurgId: string | null;
  editingSurgData: Partial<Surgery>;
  editSaving: boolean;
  setEditingSurgId: (id: string | null) => void;
  setEditingSurgData: React.Dispatch<React.SetStateAction<Partial<Surgery>>>;
  onSaveEdit: () => void;
  onDelete: (surgeryId: string) => Promise<void>;
}

export function SurgeryCard({
  surgery: surg,
  isDoctor,
  editingSurgId,
  editingSurgData,
  editSaving,
  setEditingSurgId,
  setEditingSurgData,
  onSaveEdit,
  onDelete,
}: SurgeryCardProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
    {showDeleteConfirm && (
      <DeleteConfirmModal
        title="Delete Surgery"
        description={`This will permanently delete "${surg.procedure}" (${surg.date}). This action cannot be undone.`}
        onConfirm={async () => { await onDelete(surg.id); setShowDeleteConfirm(false); }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    )}
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
      {editingSurgId === surg.id ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="block text-xs text-gray-500 mb-1">Procedure</label><input value={editingSurgData.procedure || ''} onChange={e => setEditingSurgData(d => ({ ...d, procedure: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Hospital</label><input value={editingSurgData.hospital || ''} onChange={e => setEditingSurgData(d => ({ ...d, hospital: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">Date</label><input type="date" value={editingSurgData.date || ''} onChange={e => setEditingSurgData(d => ({ ...d, date: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
            <div><label className="block text-xs text-gray-500 mb-1">EBL</label><input value={editingSurgData.ebl || ''} onChange={e => setEditingSurgData(d => ({ ...d, ebl: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Case Type</label>
              <select
                value={editingSurgData.caseType || ''}
                onChange={e => setEditingSurgData(d => ({ ...d, caseType: (e.target.value as Surgery['caseType']) }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
              >
                <option value="">— Select —</option>
                <option value="elective">Elective</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Surgical Assistant</label>
              <input
                value={editingSurgData.surgicalAssistant || ''}
                onChange={e => setEditingSurgData(d => ({ ...d, surgicalAssistant: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
              />
            </div>
          </div>
          <div><label className="block text-xs text-gray-500 mb-1">Indications</label><textarea value={editingSurgData.indications || ''} onChange={e => setEditingSurgData(d => ({ ...d, indications: e.target.value }))} rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Findings</label><textarea value={editingSurgData.findings || ''} onChange={e => setEditingSurgData(d => ({ ...d, findings: e.target.value }))} rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Complications</label><textarea value={editingSurgData.complications || ''} onChange={e => setEditingSurgData(d => ({ ...d, complications: e.target.value }))} rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
          <div><label className="block text-xs text-gray-500 mb-1">Post-Op Instructions</label><textarea value={editingSurgData.postOpInstructions || ''} onChange={e => setEditingSurgData(d => ({ ...d, postOpInstructions: e.target.value }))} rows={3} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" /></div>
          <div className="flex gap-2">
            <button onClick={onSaveEdit} disabled={editSaving} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">{editSaving ? 'Saving...' : 'Save Changes'}</button>
            <button onClick={() => setEditingSurgId(null)} className="px-3 py-1 text-xs text-gray-600">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">{surg.procedure}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{surg.date}</span>
              {isDoctor && (
                <>
                  <button onClick={() => { setEditingSurgId(surg.id); setEditingSurgData({ procedure: surg.procedure, hospital: surg.hospital, date: surg.date, ebl: surg.ebl, findings: surg.findings, complications: surg.complications, postOpInstructions: surg.postOpInstructions, anaesthesiaType: surg.anaesthesiaType, anaesthetist: surg.anaesthetist, procedureNotes: surg.procedureNotes, caseType: surg.caseType || '', surgicalAssistant: surg.surgicalAssistant || '', indications: surg.indications || '' }); }} className="p-1 text-gray-400 hover:text-blue-600" title="Edit"><Edit2 className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setShowDeleteConfirm(true)} className="p-1 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                </>
              )}
            </div>
          </div>
          {surg.hospital && <p className="text-xs text-gray-500 mt-1">{surg.hospital}</p>}
          {(surg.caseType || surg.surgicalAssistant) && (
            <p className="text-xs text-gray-500 mt-1">
              {surg.caseType && <span className={`inline-block px-2 py-0.5 rounded-full text-xs mr-2 ${surg.caseType === 'emergency' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>{surg.caseType === 'elective' ? 'Elective' : 'Emergency'}</span>}
              {surg.surgicalAssistant && <span>Assistant: {surg.surgicalAssistant}</span>}
            </p>
          )}
          {surg.indications && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Indications</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{surg.indications}</p>
            </div>
          )}
          {surg.findings && <p className="text-sm text-gray-600 mt-1">{surg.findings}</p>}
          {surg.icd10Codes?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {surg.icd10Codes.map(c => (
                <span key={c} className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-200">{c}</span>
              ))}
            </div>
          )}
          {surg.updatedBy && <p className="text-xs text-gray-400 mt-2">Edited {new Date(surg.updatedAt).toLocaleDateString()}</p>}
        </>
      )}
    </div>
    </>
  );
}
